/**
 * Compact JWT parser + RS256 signature verification via WebCrypto.
 *
 * No third-party crypto libs. The token is split, base64url-decoded by hand,
 * and verified with crypto.subtle.importKey/verify against the JWK fetched
 * from cerberus' JWKS endpoint.
 */
import { getCerberusIssuer } from './config'
import { getJwk, type FetchJwksOptions } from './jwks'
import type { AccessClaims, JsonWebKey } from './types'

export class JwtError extends Error {
  readonly code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'JwtError'
    this.code = code
  }
}

interface JwtHeader {
  alg: string
  typ?: string
  kid: string
}

function base64UrlToBytes(input: string): Uint8Array {
  // RFC 7515 base64url decode
  let s = input.replace(/-/g, '+').replace(/_/g, '/')
  const pad = s.length % 4
  if (pad === 2) s += '=='
  else if (pad === 3) s += '='
  else if (pad === 1) throw new JwtError('malformed', 'Invalid base64url length')
  const bin = atob(s)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function base64UrlToString(input: string): string {
  const bytes = base64UrlToBytes(input)
  return new TextDecoder('utf-8').decode(bytes)
}

interface ParsedJwt {
  header: JwtHeader
  payload: AccessClaims
  signingInput: string
  signature: Uint8Array
}

export function parseJwt(token: string): ParsedJwt {
  const parts = token.split('.')
  if (parts.length !== 3) {
    throw new JwtError('malformed', 'JWT must have 3 segments')
  }
  const [h, p, s] = parts
  let header: JwtHeader
  let payload: AccessClaims
  try {
    header = JSON.parse(base64UrlToString(h)) as JwtHeader
  } catch {
    throw new JwtError('malformed', 'Invalid JWT header JSON')
  }
  try {
    payload = JSON.parse(base64UrlToString(p)) as AccessClaims
  } catch {
    throw new JwtError('malformed', 'Invalid JWT payload JSON')
  }
  if (!header.kid) {
    throw new JwtError('malformed', 'JWT header missing kid')
  }
  return {
    header,
    payload,
    signingInput: `${h}.${p}`,
    signature: base64UrlToBytes(s),
  }
}

async function importRsaKey(jwk: JsonWebKey): Promise<CryptoKey> {
  // Cast through unknown — WebCrypto's lib.dom JsonWebKey shape and ours
  // differ in optional fields; the RFC fields (kty/n/e/alg) match.
  return crypto.subtle.importKey(
    'jwk',
    jwk as unknown as globalThis.JsonWebKey,
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    false,
    ['verify'],
  )
}

interface VerifyOptions {
  /** Override issuer for tests. */
  issuer?: string
  /** Allow extra clock skew (seconds). Default 30s. */
  clockSkewSeconds?: number
  /** Required `typ` claim. Default `"access"`. */
  requiredTyp?: string
  /** Injected `now` (seconds since epoch) for tests. */
  now?: number
  /** JWKS fetch options (test injection). */
  jwks?: FetchJwksOptions
}

/**
 * Parses, verifies signature, and validates claims of a cerberus access JWT.
 *
 * Throws JwtError on any failure; never returns invalid claims.
 */
export async function verifyAccessToken(
  token: string,
  opts: VerifyOptions = {},
): Promise<AccessClaims> {
  const parsed = parseJwt(token)
  if (parsed.header.alg !== 'RS256') {
    throw new JwtError('alg', `Unsupported alg: ${parsed.header.alg}`)
  }

  const jwk = await getJwk(parsed.header.kid, opts.jwks)
  if (!jwk) {
    throw new JwtError('kid', `JWK not found for kid=${parsed.header.kid}`)
  }
  const key = await importRsaKey(jwk)

  const ok = await crypto.subtle.verify(
    'RSASSA-PKCS1-v1_5',
    key,
    parsed.signature as BufferSource,
    new TextEncoder().encode(parsed.signingInput) as BufferSource,
  )
  if (!ok) {
    throw new JwtError('signature', 'JWT signature verification failed')
  }

  const claims = parsed.payload
  const now = opts.now ?? Math.floor(Date.now() / 1000)
  const skew = opts.clockSkewSeconds ?? 30
  const requiredTyp = opts.requiredTyp ?? 'access'

  if (typeof claims.exp !== 'number' || claims.exp + skew < now) {
    throw new JwtError('expired', 'JWT expired')
  }
  if (claims.typ !== requiredTyp) {
    throw new JwtError('typ', `Unexpected typ claim: ${String(claims.typ)}`)
  }
  const expectedIssuer = opts.issuer ?? getCerberusIssuer()
  if (claims.iss !== expectedIssuer) {
    throw new JwtError(
      'iss',
      `Unexpected iss: ${String(claims.iss)} (expected ${expectedIssuer})`,
    )
  }

  return claims
}

/**
 * Decodes the payload of a JWT WITHOUT verifying the signature. Used to read
 * `exp` for scheduling pre-emptive refresh — never to trust claims.
 */
export function unsafeDecodeClaims(token: string): AccessClaims | null {
  try {
    return parseJwt(token).payload
  } catch {
    return null
  }
}
